import { useState, useRef, useId, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useT } from '../hooks'

const BUBBLE_WIDTH = 300
const MARGIN = 12
const CLOSE_DELAY_MS = 150 // the pointer crosses the gap between the icon and the bubble

// The (i) in the corner of a tile or card: what the figure is and how to read it. Opens on hover, on keyboard focus and on tap;
// Escape closes it, and the pointer can move onto the bubble without it vanishing. The bubble is rendered at the end of the
// document, at a fixed spot next to the icon: a card that clips its overflow, or the card after it, can no longer cut it.
const BBookInfo = ({ label, text }) => {
  const { t } = useT()
  const id = useId()
  const button = useRef(null)
  const closing = useRef(null)
  const [box, setBox] = useState(null) // { top, left, width } once open

  const cancelClose = () => { clearTimeout(closing.current); closing.current = null }
  const close = () => { cancelClose(); setBox(null) }
  const closeSoon = () => { cancelClose(); closing.current = setTimeout(() => setBox(null), CLOSE_DELAY_MS) }

  // The page content is bounded on the left by the menu, which sits above it: measure against the page, not the screen.
  const show = () => {
    cancelClose()
    const rect = button.current?.getBoundingClientRect()
    if (!rect) return
    const page = button.current.closest('.b_book_page')?.getBoundingClientRect()
    const roomLeft = rect.right - (page ? page.left : 0) - MARGIN // bubble growing leftwards from the icon
    const roomRight = window.innerWidth - rect.left - MARGIN // growing rightwards
    const toTheLeft = roomLeft >= BUBBLE_WIDTH || roomLeft >= roomRight
    const width = Math.min(BUBBLE_WIDTH, toTheLeft ? roomLeft : roomRight)
    setBox({ top: rect.bottom + 6, left: toTheLeft ? rect.right - width : rect.left, width })
  }

  useEffect(() => { // a tap elsewhere, a scroll or a resize closes it: a fixed bubble would otherwise drift away from its icon
    if (!box) return
    const away = (e) => { if (!button.current?.parentElement.contains(e.target) && !document.getElementById(id)?.contains(e.target)) close() }
    document.addEventListener('pointerdown', away)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => { document.removeEventListener('pointerdown', away); window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close) }
  }, [box, id])

  useEffect(() => cancelClose, [])

  return (
    <span className='b_book_info' onMouseEnter={show} onMouseLeave={closeSoon}>
      <button ref={button} type='button' className='b_book_info_button' aria-label={t('pages.b_book_info.label', { label })}
        aria-expanded={!!box} aria-describedby={box ? id : undefined}
        onFocus={show} onBlur={closeSoon} onClick={() => (box ? close() : show())}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); close() } }}>
        <span aria-hidden='true'>i</span>
      </button>
      {box && createPortal(
        <span role='tooltip' id={id} className='b_book_info_bubble' style={{ top: box.top, left: box.left, width: box.width }}
          onMouseEnter={cancelClose} onMouseLeave={closeSoon}>
          {text}
        </span>,
        document.body
      )}
    </span>
  )
}

export default BBookInfo
